import { useEffect } from "react";
import { useFeatureIsOn } from "@growthbook/growthbook-react";
import { useTheme } from "@mui/material/styles";
import { JIRA_SERVICE_DESK_WIDGET } from "@platform/feature-flags";
import { useUser } from "@platform/user/client";

import envVariables from "../projectEnvVariables";

const JSD_WIDGET_BASE_URL = "https://jsd-widget.atlassian.com";
const JSD_ZINDEX_STYLE_SELECTOR = "style[data-jsd-zindex-override]";

/**
 * JiraServiceDeskWidget component
 * Conditionally loads the Atlassian JSD widget for admin and analyst users only
 * when the feature flag is enabled
 */
function JiraServiceDeskWidget(): null {
  const { user } = useUser();
  const isCentroStaff =
    user.status === "signedIn" &&
    (user.isAdmin || user.isAnalyst || user.isConsultant);
  const isFeatureEnabled = useFeatureIsOn(JIRA_SERVICE_DESK_WIDGET);
  const theme = useTheme();

  useEffect(() => {
    if (!isFeatureEnabled || !isCentroStaff) {
      return;
    }

    const helpLauncherZIndex = theme.zIndex.appBar;
    const existingStyle = document.querySelector(
      JSD_ZINDEX_STYLE_SELECTOR,
    ) as HTMLStyleElement | null;
    const style = existingStyle ?? document.createElement("style");

    style.setAttribute("data-jsd-zindex-override", "");
    style.textContent = `
      #jsd-widget,
      #jsd-widget iframe,
      #jsd-widget [style*="z-index"] {
        z-index: ${helpLauncherZIndex} !important;
      }
    `;

    if (!existingStyle) {
      document.head.appendChild(style);
    }

    // Check if script already exists to avoid duplicates
    const existingScript = document.querySelector("script[data-jsd-embedded]");

    if (existingScript) {
      return;
    }

    const script = document.createElement("script");
    script.setAttribute("data-jsd-embedded", "");
    script.setAttribute(
      "data-key",
      String(envVariables.VITE_REACT_APP_JSD_WIDGET_KEY),
    );
    script.setAttribute("data-base-url", JSD_WIDGET_BASE_URL);
    script.src = `${JSD_WIDGET_BASE_URL}/assets/embed.js`;

    // embed.js registers a DOMContentLoaded listener to initialize the
    // widget, but in an SPA that event has already fired. Rather than
    // dispatching a synthetic DOMContentLoaded (which could trigger
    // unrelated listeners elsewhere in the app), we temporarily
    // intercept addEventListener to capture the widget's specific
    // handler and invoke it directly after the script loads.
    const originalAddEventListener: typeof document.addEventListener =
      document.addEventListener.bind(
        document,
      ) as typeof document.addEventListener;
    let widgetInitHandler: EventListener | null = null;

    document.addEventListener = (
      type: string,
      handler: EventListenerOrEventListenerObject,
      options?: boolean | AddEventListenerOptions,
    ): void => {
      if (type === "DOMContentLoaded") {
        widgetInitHandler = handler as EventListener;
      }
      originalAddEventListener(type, handler, options);
    };

    script.onload = () => {
      // Restore the original addEventListener immediately
      document.addEventListener = originalAddEventListener;

      // Invoke only the widget's init handler, not a global event
      if (widgetInitHandler) {
        widgetInitHandler(new Event("DOMContentLoaded"));
      }
    };

    document.body.appendChild(script);

    return () => {
      // Restore addEventListener in case the script hasn't loaded yet
      document.addEventListener = originalAddEventListener;

      // Remove the injected script
      script.remove();

      // Remove any widget iframe/elements the script may have created
      const widgetContainer = document.getElementById("jsd-widget");
      if (widgetContainer) {
        widgetContainer.remove();
      }

      const styleTag = document.querySelector(JSD_ZINDEX_STYLE_SELECTOR);
      if (styleTag) {
        styleTag.remove();
      }
    };
  }, [isFeatureEnabled, theme.zIndex.appBar, isCentroStaff]);

  return null;
}

export default JiraServiceDeskWidget;
